import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RASHIS, NAKSHATRAS } from "@/data/jharkhand";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Kundli() {
  const { user } = useAuth();
  const [boy, setBoy] = useState({ rashi: "Mesha", nakshatra: "Ashwini" });
  const [girl, setGirl] = useState({ rashi: "Mesha", nakshatra: "Ashwini" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Prefill from own profile
  const prefillSelf = () => {
    if (!user) return;
    const slot = user.gender === "male" ? setBoy : setGirl;
    slot({ rashi: user.rashi || "Mesha", nakshatra: user.nakshatra || "Ashwini" });
    toast.success("Filled from your profile");
  };

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/kundli/match", {
        boy_rashi: boy.rashi, boy_nakshatra: boy.nakshatra,
        girl_rashi: girl.rashi, girl_nakshatra: girl.nakshatra,
      });
      setResult(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="container-px py-8 max-w-5xl">
      <span className="label-cap">Kundli</span>
      <h1 className="font-heading text-4xl text-primary mt-1">Ashtakoot Compatibility</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">A traditional 36-Guna compatibility score based on Nakshatra and Rashi. Use as a helpful indicator alongside personal connection.</p>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Card title="Groom (Boy)">
          <KSel label="Rashi" value={boy.rashi} onChange={(v) => setBoy({ ...boy, rashi: v })} items={RASHIS} testid="kundli-boy-rashi" />
          <KSel label="Nakshatra" value={boy.nakshatra} onChange={(v) => setBoy({ ...boy, nakshatra: v })} items={NAKSHATRAS} testid="kundli-boy-nakshatra" />
        </Card>
        <Card title="Bride (Girl)">
          <KSel label="Rashi" value={girl.rashi} onChange={(v) => setGirl({ ...girl, rashi: v })} items={RASHIS} testid="kundli-girl-rashi" />
          <KSel label="Nakshatra" value={girl.nakshatra} onChange={(v) => setGirl({ ...girl, nakshatra: v })} items={NAKSHATRAS} testid="kundli-girl-nakshatra" />
        </Card>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={compute} disabled={loading} className="rounded-full bg-primary hover:bg-primary/90" data-testid="kundli-compute">
          <Sparkles className="w-4 h-4 mr-1"/>Check compatibility
        </Button>
        {user?.rashi && (
          <Button variant="outline" onClick={prefillSelf} className="rounded-full" data-testid="kundli-prefill">Use my profile</Button>
        )}
      </div>

      {result && (
        <div className="mt-8 card-soft p-6 sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="label-cap">Result</span>
              <h2 className="font-heading text-5xl text-primary mt-1">{result.total} <span className="text-2xl text-muted-foreground">/ {result.max} guna</span></h2>
              <div className={`mt-2 inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                result.compatibility === "Excellent" ? "bg-emerald-100 text-emerald-700" :
                result.compatibility === "Good" ? "bg-secondary/20 text-secondary" :
                result.compatibility === "Fair" ? "bg-amber-100 text-amber-700" : "bg-destructive/15 text-destructive"
              }`} data-testid="kundli-verdict">{result.compatibility}</div>
            </div>
            <div className="text-sm text-muted-foreground max-w-xs">
              {result.total >= 18 ? "A traditionally compatible match." : "Low compatibility per Ashtakoot. Consider consulting a Pandit."}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {Object.entries(result.breakdown).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border/70 p-4 bg-accent/30">
                <div className="flex justify-between items-baseline">
                  <span className="font-medium">{k}</span>
                  <span className="text-sm text-muted-foreground">{v.score} / {v.max}</span>
                </div>
                <div className="h-1.5 bg-muted rounded mt-2 overflow-hidden">
                  <div className="h-full bg-secondary" style={{ width: `${(v.score / v.max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="card-soft p-6">
      <h3 className="font-heading text-xl text-primary">{title}</h3>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}
function KSel({ label, value, onChange, items, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{items.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
